/**
 * Ensemble des validateurs disponibles pour les formulaires
 */

import { dateTimeFormatFr, dateTimeFormatIso } from '@/utils/data/date'
import { APP_HOLIDAY_DEFAULT_LOCALE } from '@/consts/app'
import store from '@/store'

/**
 * Validateur champ obligatoire
 * @param val
 * @returns {boolean|string}
 */
export const required = val => !!(Array.isArray(val) ? val.length : val) || 'Obligatoire'

/**
 * Validateur longueur
 * @param l - la longueur attendue
 * @param message - Un message personnalisé
 * @returns {function(*=): *}
 */
export const length = (l, message) =>
  optional(val =>
    (
      !Array.isArray(val) ||
      l === val.reduce((c, v) => v ? c + 1 : c, 0)
    ) ||
    (message || `Doit contenir ${l} éléments`)
  )

/**
 * Validateur champ non undefined
 * @param val
 * @returns {boolean|string}
 */
export const defined = val => val !== undefined || 'Obligatoire'

/**
 * Validateur champ not nill
 * @param val
 * @returns {boolean|string}
 */
export const notNil = val => !_.isNil(val) || 'Obligatoire'

/**
 * Outil pour créer des validateurs optionnels
 * Si le champ est obligatoire, required va trigger avant ce validateur
 * @param f
 * @returns {function(*=): *}
 */
export const optional = (f) => (val) =>
  val
    ? f(val)
    : true

/**
 * Vérification nombre entier
 * @param regexp
 * @returns {function(*=): *}
 */
export const integer = optional((val) => Number.isInteger(val) || 'Nombre entier uniquement')

/**
 * Factory de validateur depuis une regexp
 * @param regexp
 * @param errorLabel
 * @returns {function(*=): *}
 */
export const regexp = (regexp, errorLabel) =>
  optional((val) => regexp.test(val) || (errorLabel || 'Format invalide'))

/**
 * Valide un champs à l'aide d'une regexp. Retourne le message formaté.
 * @param spec
 * @returns {function(*=): *}
 */
export const frenchRegexp = (spec) => (value) =>
  regexp(spec.regex, `Le format du champs n'est pas respecté, format attendu [${spec.label}]`)(value)

/**
 * Validateur qui ne s'applique qu'aux agences françaises
 * @param validator
 * @returns {(function(*=): (boolean|*))|*}
 */
export const frenchValidator = (validator) => (value) =>
  store.getters['user/company'].holidayLocale !== APP_HOLIDAY_DEFAULT_LOCALE || validator(value)

/**
 * Date supérieure à la date fournie
 * @param date
 * @returns {function(*): boolean|string}
 */
export const gtDate = date =>
  optional(
    val =>
      val > date ||
      (date
        ? `Doit être supérieure à ${dateTimeFormatFr(date)}`
        : `La date de comparaison n'est pas définie`)
  )

/**
 * Date supérieure ou égale à la date fournie
 * @param date
 * @returns {function(*): boolean|string}
 */
export const gteDate = date =>
  optional(
    val =>
      val >= date ||
      (date
        ? `Doit être supérieure ou égale à ${dateTimeFormatFr(date)}`
        : `La date de comparaison n'est pas définie`)
  )

/**
 * Date inférieure à la date fournie
 * @param date
 * @returns {function(*): boolean|string}
 */
export const ltDate = date =>
  optional(
    val =>
      val < date ||
      (date
        ? `Doit être inférieure à ${dateTimeFormatFr(date)}`
        : `La date de comparaison n'est pas définie`)
  )

/**
 * Date inférieure ou égale à la date fournie
 * @param date
 * @returns {function(*): boolean|string}
 */
export const lteDate = date =>
  optional(
    val =>
      val <= date ||
      (date
        ? `Doit être inférieure ou égale à ${dateTimeFormatFr(date)}`
        : `La date de comparaison n'est pas définie`)
  )

/**
 * Date supérieure à la date actuelle
 * @type {function(*=): boolean|string}
 */
export const gtNow = optional(val => {
  return val > dateTimeFormatIso(new Date()) || `Doit être supérieur à ${dateTimeFormatFr(new Date())}`
})

/**
 * Valeur maximale ou égale
 * @param max
 * @returns {function(*=): *}
 */
export const lte = (max) =>
  optional(val => {
    return max >= val || `Doit être inférieur à ${max}`
  })

/**
 * Valeur minimale
 * @param min
 * @returns {function(*=): *}
 */
export const gt = (min) =>
  optional(val => {
    return min <= val || `Doit être supérieur à ${min}`
  })

/**
 * Validateur format email
 * @type {function(*=): *}
 */
export const email = regexp(/(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/)

/**
 * Validateur de numéro de sécurité sociale
 * @type {function(*=): *}
 */
export const securiteSociale = regexp(/^([123478])([0-9]{2})(0[1-9]|1[0-2]|3[1-9]|4[0-2]|[5-9][0-9]|20)([0][1-9]|2[AB]|[1-9][0-9])([0-9]{3})(00[1-9]|0[1-9][0-9]|[1-9][0-9]{2})(0[1-9]|[1-8][0-9]|9[0-7])?$/)

export const creditCard = regexp(/^(?:\d{4}\s){3}\d{4}$/)

export const creditCardExpireat = regexp(/^\d{2}\/(?:\d{4}|\d{2})$/)

export const creditCardCcv = regexp(/^\d{3}$/)
